import type { AufmassKey } from '@/i18n/messages/aufmass'

export interface PositionColumn {
  exportColumn: number
  /** The workbook's exact header, newlines and all. Collapse for display; never edit. */
  label: string
  group: string
  sortKey: number
  isMeter: boolean
  source: 'answers' | 'hausanschluss'
}

export interface AufmassRow {
  sessionId: string
  workDate: string
  isoWeek: number
  kolonaLeaderName: string | null
  bauleiterName: string | null
  companyName: string | null
  firmaType: string | null
  city: string | null
  providerName: string | null
  streets: string | null
  pops: string | null
  dps: string | null
  comment: string | null
  workerCount: number
  totalManHours: number
  totalMetri: number
  moneySub: number
  moneyMih: number
  pricelistSubName: string | null
  pricelistMihName: string | null
  hasInstance: boolean
  /** Why the row needs a human — see FLAG_REASON — or null when nothing is wrong. */
  flagReason: string | null
  /** 'instance' | 'timelog' | 'generated' — see PRESENCE below. */
  presence: string | null
  projectName: string | null
  projectCode: string | null
  rom: string | null
  /** Quantity per workbook column index, already collapsed from question level. */
  positions: Record<string, number>
  /**
   * Cells a person has corrected. The corrected value is ALREADY applied to the fields
   * above — this is the provenance, so a cell can be marked and its original shown.
   */
  overrides: Record<string, OverrideCell>
  /**
   * Metres Total Metri moved because an RTK measurement was re-drawn after the fact, or null
   * when no RTK edit touches this crew-day. Non-null → the row is highlighted as changed.
   */
  rtkMetriDelta: number | null
}

export interface OverrideCell {
  value: string | null
  original: string | null
  editedBy: string
  editedAt: string
}

/**
 * The columns the GIS grid may correct, mapped to the workbook's own header for each.
 *
 * Deliberately all descriptive: none of them feeds a total, so a correction can never put
 * the stat tiles and the grid in disagreement. Quantities are excluded on purpose — they
 * drive Total Metri and the price-resolved money columns. The server owns this list
 * (EditableFields.cs); this copy exists so the grid can render read-only cells as read-only
 * rather than discovering it on a 400.
 */
export const EDITABLE_FIELDS: Record<string, string> = {
  bauleiter_name: 'Polir',
  kolona_leader_name: 'Vodja kolone',
  city: 'MJESTO',
  provider_name: 'Provider',
  streets: 'ULICA TRASA',
  pops: 'POP',
  dps: 'DP',
  comment: 'Izvjestaj',
  rom: 'ROM',
}

export interface AufmassTotals {
  sessions: number
  totalMetri: number
  moneySub: number
  moneyMih: number
  manHours: number
}

export interface AufmassPage {
  rows: AufmassRow[]
  total: number
  columns: PositionColumn[]
  pageTotals: AufmassTotals
}

export interface FilterOptions {
  dateBounds: { from: string | null; to: string | null }
  projects: { id: string; name: string; code: string | null }[]
  companies: { id: string; name: string; type: string }[]
  crews: { id: string; name: string }[]
  /** Cabling positions (RV07 multiples + Synergy), value = position export column. */
  cables: { value: string; label: string }[]
}

/** Workbook headers carry literal newlines; collapse them for a table cell. */
export function tidyLabel(label: string): string {
  return label.replace(/\s+/g, ' ').trim()
}

/**
 * How a crew-day came to exist, and what each state means on screen.
 *
 * `generated` is Aufmaß asserting a row AU has no record of, because the crew was active
 * either side of that day and somebody worked it. It is inference, so it is labelled as
 * inference — the workbook fills the same gaps silently, which is what makes its row count
 * higher than anything AU can justify.
 */
export const PRESENCE: Record<string, { label: AufmassKey; title: AufmassKey; muted: boolean }> = {
  instance:  { label: 'presenceReport',   title: 'presenceReportTitle',   muted: false },
  timelog:   { label: 'presenceClocked',  title: 'presenceClockedTitle',  muted: false },
  generated: { label: 'presenceInferred', title: 'presenceInferredTitle', muted: true },
}

/**
 * Why a crew-day is marked for resolution.
 *
 * Each carries a label AND an icon, so the state never depends on the red row tint alone —
 * that matters for colourblind readers, greyscale printing and forced-colors mode. The
 * `detail` spells out the rule, because a mark the reader cannot check is just an accusation.
 * Both are message keys (aufmassMessages), translated where they are shown.
 */
export const FLAG_REASON: Record<string, { label: AufmassKey; detail: AufmassKey }> = {
  quantity: { label: 'flagMarked', detail: 'flagQuantityDetail' },
  negative_hours: { label: 'flagMarked', detail: 'flagNegativeHoursDetail' },
}
